import DOMPurify from "dompurify";
import type { IChatAttachment, IChatMessage } from "@/types/(my-space)/chat.interface";
import { stripMarkdown } from "./markdown";

/**
 * A project ticket made from chat messages: its suggested title and the
 * description — the messages quoted in order, each with who wrote it and
 * when. Ticket descriptions are stored and shown as HTML, so every bit of
 * chat text is escaped here and the result is sanitized on top.
 */

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** Plain text as paragraphs; single line breaks kept. */
const textHtml = (text: string) =>
  text
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("");

const isHttp = (url: string | undefined | null) => !!url && /^https?:\/\//i.test(url);

const link = (url: string, label: string) =>
  `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`;

export interface TicketMessageLabels {
  /** "Sara Ali" — the sender, as the chat shows them. */
  senderName: (m: IChatMessage) => string;
  /** "Oct 20, 2026, 3:22 PM". */
  formatTime: (iso: string) => string;
  /** What a non-text message is ("Photo", "Location", …) when it has no words. */
  contentLabel: (m: IChatMessage) => string;
  /** "From the chat “Design team”". */
  fromChat: string;
  /**
   * Where the ticket keeps a chat file (its own copy, once copied); absent
   * or undefined: the chat's file.
   */
  fileUrl?: (a: IChatAttachment) => string | undefined;
}

const isImage = (a: IChatAttachment) => a.kind === "image" || a.mimeType?.startsWith("image/");

const image = (url: string, alt: string) =>
  `<p><img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}"></p>`;

/**
 * The words a message stands for, as plain text: its text or caption (the
 * Markdown symbols dropped), else what it is.
 */
export const messagePlainText = (m: IChatMessage, contentLabel: (m: IChatMessage) => string) => {
  const body = m.body?.trim() ? stripMarkdown(m.body) : "";
  return body || contentLabel(m);
};

/** First line of the first message with words, as a title (≤ 120 chars). */
export const suggestTicketTitle = (
  messages: IChatMessage[],
  contentLabel: (m: IChatMessage) => string,
  fallback: string,
) => {
  for (const m of messages) {
    const line = messagePlainText(m, contentLabel).split("\n").map((l) => l.trim()).find(Boolean);
    if (line) return line.length > 120 ? `${line.slice(0, 117).trimEnd()}…` : line;
  }
  return fallback;
};

/** One quoted message: who · when, its words, then its files / link / place. */
const messageHtml = (m: IChatMessage, labels: TicketMessageLabels) => {
  const parts = [
    `<p><strong>${escapeHtml(labels.senderName(m))}</strong> · ${escapeHtml(labels.formatTime(m.createdAt))}</p>`,
    textHtml(messagePlainText(m, labels.contentLabel)),
  ];
  const extras: string[] = [];
  const images: string[] = [];
  for (const a of m.attachments ?? []) {
    const url = labels.fileUrl?.(a) ?? a.fileUrl;
    if (!isHttp(url)) continue;
    // Pictures show in the ticket; other files are links.
    if (isImage(a)) images.push(image(url, a.fileName || ""));
    else extras.push(link(url, a.fileName || url));
  }
  const meta = m.metadata ?? {};
  if (meta.link?.url && isHttp(meta.link.url)) extras.push(link(meta.link.url, meta.link.title || meta.link.url));
  if (meta.location) {
    const { latitude, longitude, name } = meta.location;
    extras.push(
      link(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, name || `${latitude}, ${longitude}`),
    );
  }
  parts.push(...images);
  if (extras.length) parts.push(`<ul>${extras.map((e) => `<li>${e}</li>`).join("")}</ul>`);
  return `<blockquote>${parts.join("")}</blockquote>`;
};

/**
 * The ticket's description: the person's own notes (if any), then where the
 * messages came from and the messages themselves, oldest first.
 */
export const ticketDescriptionHtml = (
  messages: IChatMessage[],
  labels: TicketMessageLabels,
  notes?: string,
): string => {
  const ordered = [...messages].sort((a, b) => a.id - b.id);
  const html = [
    notes?.trim() ? textHtml(notes) : "",
    `<p><em>${escapeHtml(labels.fromChat)}</em></p>`,
    ...ordered.map((m) => messageHtml(m, labels)),
  ].join("");
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "blockquote", "ul", "li", "a", "img"],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt"],
  });
};
